import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import {
  readNotification,
  readNotificationsInRange,
  unreadNotification,
  getNotificationsCount,
  getNotificationsList,
} from "../../actions/notification";
import { getNotificationParams } from "./helper";
import { getBucketRange } from "./dateBuckets";
import { successReadNotificationToast } from "@Features/issues/state/bcfData.actions";
import type { RootState } from "@Reducers/index";
import type { CustomModuleItem } from "@ServerTypes/portal/portal.schema";
import type {
  GetNotificationsResponse,
  NotificationItem,
  NotificationsPage,
} from "@ServerTypes/notifications/notifications.schema";
import { formNotificationTypeLabels } from "../../../../helpers/constants";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

const PAGE_SIZE = 20;

const DEFAULT_ICON_TYPE_MAP: { [key: string]: string } = {
  Transmittal: "Transmittals",
  Setting: "SettingsIcon",
  Space: "SpacesIcon",
  Issue: "IssuesIcon",
  File: "FilesIcon",
  Task: "TasksIcon",
  Estimate: "EstimateIcon",
  Review: "Review",
  Mailbox: "Mailbox",
  Chat: "Chat",
  [formNotificationTypeLabels.inspections]: "inspectionForm",
  [formNotificationTypeLabels.bills]: "bills",
  [formNotificationTypeLabels.progressUpdates]: "ProgressUpdates",
  [formNotificationTypeLabels.changeOrders]: "changeOrder",
  Support: "support",
};

/** One rendered notification, exactly as `getNotificationParams` derives it from a raw record. */
export type NotificationEntry = ReturnType<typeof getNotificationParams>;

/**
 * `POST /getNotifications` answers with one of TWO bodies (server, notifications.schema.ts):
 * the default paginated page `{ status, result, count? }` or the header-bell `{ new, previous }`.
 * `getNotificationsResponse` is now the union of the two, so `result` lives on the page member only
 * and this is the mode narrowing — not a shape guard. `getNotificationsList` never sets
 * `value.source`, so the page member is always what comes back; the check is what lets TypeScript
 * prove it, and `result` is typed `NotificationItem[]` once it has.
 *
 * (The former version of this comment called `result` `z.unknown()` and this function a runtime
 * array check. Both were true of the old schema and neither is now.)
 */
function isNotificationPage(body: GetNotificationsResponse): body is NotificationsPage {
  return "result" in body;
}

/**
 * Axios reports an aborted request as an error, and a catch binding may only be annotated `any` or
 * `unknown`, so the two fields that identify one are read behind an `in` narrowing.
 */
function isCanceledError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  return ("name" in err && err.name === "CanceledError") || ("code" in err && err.code === "ERR_CANCELED");
}

interface UseAllNotificationOptions {
  /** Element the <Trans> `bold` slot renders inside each notification's text. */
  Bold?: React.ElementType;
}

export function useAllNotification({ Bold }: UseAllNotificationOptions = {}) {
  const dispatch = useDispatch();
  const customModules = useSelector((state: RootState) => state.customModules);
  const [iconTypeMap, setIconTypeMap] = useState<{ [key: string]: string }>({ ...DEFAULT_ICON_TYPE_MAP });

  useEffect(() => {
    const next = { ...DEFAULT_ICON_TYPE_MAP };
    // `resourceType`/`icon` are optional on customModuleItemSchema but always set on a saved
    // module, and this map is `Record<string, string>`. Erased assertions.
    customModules.forEach((module: CustomModuleItem) => {
      next[module.resourceType!] = module.icon!;
    });
    setIconTypeMap(next);
  }, [customModules]);

  const [notifications, setNotifications] = useState<NotificationEntry[]>([]);
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<ObjectIdString>>(new Set());
  const loadAbortRef = useRef<AbortController | null>(null);

  const applyPage = useCallback(
    (skip: number, result: NotificationItem[]) => {
      const items = result.map((n) => getNotificationParams(n, Bold));
      setNotifications((prev) => {
        if (skip === 0) return items;
        const seen = new Set(prev.map((n) => n.id));
        return [...prev, ...items.filter((n) => !seen.has(n.id))];
      });
      setHasMore(result.length === PAGE_SIZE);
      setHasInitialLoaded(true);
    },
    [Bold],
  );

  const loadPage = useCallback(
    async (skip: number) => {
      loadAbortRef.current?.abort();
      const controller = new AbortController();
      loadAbortRef.current = controller;
      try {
        const response = await getNotificationsList(skip, PAGE_SIZE, controller.signal);
        if (controller.signal.aborted) return;
        applyPage(skip, isNotificationPage(response) ? response.result : []);
      } catch (err: unknown) {
        if (isCanceledError(err) || controller.signal.aborted) return;
        throw err;
      }
    },
    [applyPage],
  );

  useEffect(() => {
    // Fire-and-forget: the effect only seeds the notification list.
    void loadPage(0);
    return () => {
      loadAbortRef.current?.abort();
      loadAbortRef.current = null;
    };
  }, [loadPage]);

  const loadMore = useCallback(() => {
    // Fire-and-forget pagination: it only appends to the list state.
    void loadPage(notifications.length);
  }, [loadPage, notifications.length]);

  const dismissNotification = useCallback(
    async (id: ObjectIdString) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      const response = await readNotification([id]);
      if (response) {
        // Fire-and-forget thunks: they refresh the badge count and raise a toast.
        void dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
        void dispatch(successReadNotificationToast([id]));
      }
    },
    [dispatch],
  );

  const toggleReadState = useCallback(
    async (id: ObjectIdString, currentRead: string) => {
      const willBeRead = currentRead === "NOTIFICATION_UNREAD";
      if (willBeRead) setNotifications((prev) => prev.filter((n) => n.id !== id));
      const response = willBeRead ? await readNotification([id]) : await unreadNotification([id]);
      if (response) {
        // Fire-and-forget thunks: they refresh the badge count and raise a toast.
        void dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
        if (willBeRead) void dispatch(successReadNotificationToast([id]));
      }
    },
    [dispatch],
  );

  const toggleSelect = useCallback((id: ObjectIdString) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(notifications.map((n) => n.id)));
  }, [notifications]);

  const selectedItems = notifications.filter((n) => selectedIds.has(n.id));
  const selectedUnreadCount = selectedItems.filter((n) => n.read === "NOTIFICATION_UNREAD").length;
  const majorityIsUnread = selectedUnreadCount >= selectedItems.length - selectedUnreadCount;
  const allSelected = notifications.length > 0 && selectedIds.size === notifications.length;

  const markSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const asRead = majorityIsUnread;
    const willBeEmpty = asRead && ids.length === notifications.length;
    if (asRead) setNotifications((prev) => prev.filter((n) => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
    const response = asRead ? await readNotification(ids) : await unreadNotification(ids);
    if (response) {
      // Fire-and-forget thunks: they refresh the badge count and raise a toast.
      void dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
      if (asRead) void dispatch(successReadNotificationToast(ids));
    }
    // Fire-and-forget re-sync: it only refills the list state.
    if (willBeEmpty) void loadPage(0);
  }, [selectedIds, majorityIsUnread, notifications.length, dispatch, loadPage]);

  const markGroupRead = useCallback(
    async (bucketKey: string, ids: ObjectIdString[]) => {
      if (ids.length === 0) return;
      // Mark the entire group as read on the backend via its date range, so
      // notifications in this group that haven't been paginated in yet are read
      // too. Fall back to the loaded ids if the bucket has no resolvable range.
      const { fromDate, toDate } = getBucketRange(bucketKey);
      const response =
        fromDate != null || toDate != null
          ? await readNotificationsInRange(fromDate, toDate)
          : await readNotification(ids);
      if (response) {
        // Fire-and-forget thunks: they refresh the badge count and raise a toast.
        void dispatch(getNotificationsCount({ read: "NOTIFICATION_UNREAD" }));
        void dispatch(successReadNotificationToast(ids));
      }
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      // The range can mark items that aren't currently loaded, so a local
      // removal alone can't reflect the true unread list. Re-sync from the
      // server to show exactly what's still unread (and refill the page).
      // Fire-and-forget re-sync: it only refills the list state.
      void loadPage(0);
    },
    [dispatch, loadPage],
  );

  const showEmptyState = hasInitialLoaded && notifications.length === 0;

  return {
    notifications,
    showEmptyState,
    selectedIds,
    allSelected,
    majorityIsUnread,
    iconTypeMap,
    hasMore,
    loadMore,
    dismissNotification,
    toggleReadState,
    toggleSelect,
    selectAll,
    clearSelection,
    markSelected,
    markGroupRead,
  };
}
